/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";
import { StreamingAccount } from "./streaming-account";
import { EownerType, Subscription, SubscriptionItemInfo } from "./subscription";
import { Supplier } from "./supplier";

export enum EstreamingServiceStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum EPricePlanType {
  SLOT = "SLOT",
  FULL_ACCOUNT = "FULL_ACCOUNT",
}

export interface PricePlan {
  id: number;
  name: string;
  price: number;
  duration: number;
  status: EstreamingServiceStatus;
  isDefault: boolean;
  pricePlanType: EPricePlanType;
  supplierId?: number;
  supplier?: Supplier;
  serviceId?: number;
  ownerType?: EownerType;
}

export interface StreamingService {
  id: number;
  accountCode: string;
  name: string;
  color: string;
  icon: string;
  status: "ACTIVE" | "INACTIVE";
  pricePlans: PricePlan[];
  // streamingAccounts: StreamingAccount[];
  // subscriptionItems: {
  //   id: string;
  //   subscription: { price: number; status: string; daysRemaining: number };
  // }[];
  accountsCount?: number;
  availableSlotsCount?: number;
  occupiedSlotsCount?: number;
  activeSubscriptionsCount?: number;
  monthlyRevenue?: number;
  avgPrice?: number;
  createdAt: string;
  updatedAt: string;
}

// Tipos para las peticiones
export interface RegisterStreamingServiceRequest {
  accountCode: string;
  name: string;
  color: string;
  icon: string;
  status?: EstreamingServiceStatus;
  pricePlans?: PricePlanRequest[];
}

export interface PricePlanRequest {
  name: string;
  price: number;
  duration: number;
  status?: EstreamingServiceStatus;
  isDefault?: boolean;
  ownerType?: EownerType;
  supplierId?: number;
  pricePlanType: EPricePlanType;
}

export interface PricePlanUpdateRequest extends PricePlanRequest {
  updateSub?: boolean;
}

export interface UpdateStreamingServiceRequest {
  accountCode?: string;
  name?: string;
  color?: string;
  icon?: string;
  status?: EstreamingServiceStatus;
  pricePlans?: PricePlanRequest[];
}

export interface IfilterStreamingServices {
  status?: EstreamingServiceStatus;
  name?: string;
  page: number;
  limit: number;
}

/**
 * Elimina campos vacíos de un objeto
 */
function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

/**
 * Servicio para gestionar servicios de streaming
 */
export const streamingServiceApi = {
  /**
   * Registrar un nuevo servicio de streaming
   */
  async register(
    data: RegisterStreamingServiceRequest,
  ): Promise<IresponseData<StreamingService>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<StreamingService>>(
        "/streaming-services",
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el servicio. Por favor, intenta de nuevo.",
      );
    }
  },

  async registerPricePlan(serviceId: number, data: PricePlanRequest) {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<StreamingService>>(
        `/streaming-services/${serviceId}/price-plans`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el plan de precios. Por favor, intenta de nuevo.",
      );
    }
  },

  async updatePricePlan(pricePlanId: number, data: PricePlanUpdateRequest) {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<StreamingService>>(
        `/streaming-services/${pricePlanId}/price-plans`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el plan de precios. Por favor, intenta de nuevo.",
      );
    }
  },

  async deletePricePlan(pricePlanId: number) {
    try {
      const response = await apiClient.delete<IresponseData<StreamingService>>(
        `/streaming-services/${pricePlanId}/price-plans`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al eliminar el plan de precios. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener todos los servicios de streaming
   */
  async getAll(
    filters?: IfilterStreamingServices,
  ): Promise<IresponseDataPaginated<StreamingService[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<
        IresponseDataPaginated<StreamingService[]>
      >("/streaming-services", { params: cleanFilters });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los servicios. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener un servicio de streaming por ID
   */
  async getById(id: number): Promise<IresponseData<StreamingService>> {
    try {
      const response = await apiClient.get<IresponseData<StreamingService>>(
        `/streaming-services/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener el servicio. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Actualizar un servicio de streaming
   */
  async update(
    id: string,
    data: UpdateStreamingServiceRequest,
  ): Promise<IresponseData<StreamingService>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<StreamingService>>(
        `/streaming-services/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el servicio. Por favor, intenta de nuevo.",
      );
    }
  },
};
